import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Loader2, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) setError('Geen reset token gevonden');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Wachtwoorden komen niet overeen');
      return;
    }
    
    // Validate password policy
    if (password.length < 8) {
      setError('Wachtwoord moet minimaal 8 tekens bevatten met minstens 1 hoofdletter, 1 kleine letter en 1 cijfer.');
      return;
    }
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasDigit = /[0-9]/.test(password);
    if (!hasUpper || !hasLower || !hasDigit) {
      setError('Wachtwoord moet minimaal 8 tekens bevatten met minstens 1 hoofdletter, 1 kleine letter en 1 cijfer.');
      return;
    }

    setLoading(true);
    try {
      const res = await authAPI.resetPassword(token, password);
      const data = await res.clone().json();
      if (!res.ok) throw new Error(data.detail || 'Reset mislukt');
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-10 h-10 rounded-xl bg-[#1a2332] flex items-center justify-center">
            <Shield className="w-6 h-6 text-[#d4a853]" />
          </div>
          <span className="text-xl font-bold text-[#1a2332]">TW1 Group</span>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          {success ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
              <h2 className="text-xl font-semibold text-[#1a2332] mb-2">Wachtwoord bijgewerkt</h2>
              <p className="text-sm text-slate-500 mb-6">Je kunt nu inloggen met je nieuwe wachtwoord.</p>
              <Link to="/login">
                <Button className="bg-[#1a2332] hover:bg-[#111827] text-white rounded-xl" data-testid="reset-password-to-login">
                  Ga naar inloggen
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-semibold text-[#1a2332] mb-1">Wachtwoord resetten</h2>
              <p className="text-sm text-slate-500 mb-6">Kies een nieuw wachtwoord voor je account.</p>

              {error && (
                <Alert variant="destructive" className="mb-4" data-testid="reset-password-token-state">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">Nieuw wachtwoord</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="h-11 pr-10"
                      data-testid="reset-password-new-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Bevestig wachtwoord</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="h-11"
                    data-testid="reset-password-confirm-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading || !token}
                  className="w-full h-11 bg-[#1a2332] hover:bg-[#111827] text-white rounded-xl"
                  data-testid="reset-password-submit-button"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Bezig...</>
                  ) : (
                    'Wachtwoord wijzigen'
                  )}
                </Button>
              </form>
            </>
          )}
        </div>

        <p className="text-center mt-4">
          <Link to="/login" className="text-sm text-[#d4a853] hover:text-[#c79b45] transition-colors duration-150">
            Terug naar inloggen
          </Link>
        </p>
      </div>
    </div>
  );
}
